import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { UploadDialog } from "./UploadDialog";
import { useUploadQueue, type UploadPlace } from "./upload-queue";

type AddUploads = (files: File[], place: UploadPlace) => void;

const Uploads = createContext<AddUploads | null>(null);
/** How far the tray reaches up from the window's bottom edge, in px; 0 while it does not show. */
const TrayTop = createContext(0);

/**
 * The app's one upload queue and its tray (spec upload-tray), as live's global store and its
 * UploadDialog: mounted with every signed-in page (`AppLayout`), so an upload goes on and its row
 * stays as the user leaves the library for another page, brand or team.
 */
export function UploadsProvider({ children }: { children: ReactNode }) {
  const queue = useUploadQueue();
  // A page reads `add` alone, kept the same function, so a progress step renders the tray and
  // never the page that started the upload.
  const latest = useRef(queue.add);
  useEffect(() => {
    latest.current = queue.add;
  });
  const add = useCallback<AddUploads>((files, place) => void latest.current(files, place), []);
  // The tray's top in a context of its own, so as the tray moves only what stands above it renders.
  const [trayTop, setTrayTop] = useState(0);
  return (
    <Uploads.Provider value={add}>
      <TrayTop.Provider value={trayTop}>
        {children}
        <UploadDialog queue={queue} onTop={setTrayTop} />
      </TrayTop.Provider>
    </Uploads.Provider>
  );
}

/** Queues files into a library (its team and brand); the app's tray shows them going up. */
export function useAddUploads(): AddUploads {
  const add = useContext(Uploads);
  if (add === null) throw new Error("useAddUploads needs UploadsProvider, which AppLayout mounts.");
  return add;
}

/**
 * How far the upload tray reaches up from the window's bottom edge, in px, 0 while it does not
 * show: what else stands in the bottom-right corner, the unlock card, stands above it (spec
 * web/unlock-card-above-uploads).
 */
export function useUploadTrayTop(): number {
  return useContext(TrayTop);
}
